import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, TrendingUp, Users, BarChart3 } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useRole } from '@/hooks/useRole';
import { toast } from 'sonner';
import { MeetingKPITracker } from '@/components/meetings/MeetingKPITracker';

interface MentoringStats {
  meeting_type: string;
  total_meetings: number;
  total_minutes: number;
  avg_duration: number;
}

interface MonthlyData {
  month: string;
  mentoring_minutes: number;
  coaching_minutes: number;
  observation_minutes: number;
  total_meetings: number;
}

export function MentoringReport() {
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [selectedUser, setSelectedUser] = useState(user?.id || '');
  const [stats, setStats] = useState<MentoringStats[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [teacherSearch, setTeacherSearch] = useState('');
  const [showTeacherResults, setShowTeacherResults] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<any>(null);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    } else {
      // For non-admin users, load their profile data
      if (user?.id) {
        loadCurrentUserProfile();
      }
    }
    if (selectedUser) {
      loadReportData();
    }
  }, [selectedUser, selectedPeriod, isAdmin, user?.id]);

  const loadCurrentUserProfile = async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      
      if (data) {
        setSelectedTeacher(data);
        setSelectedUser(data.id);
        setTeacherSearch(`${data.first_name} ${data.last_name} (${data.email})`);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      toast.error('Failed to load user profile');
    }
  };

  const loadUsers = async (searchQuery = '') => {
    try {
      let query = supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .order('last_name');

      if (searchQuery.trim()) {
        query = query.or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`);
      }

      // Limit results to prevent overwhelming the UI
      query = query.limit(50);

      const { data, error } = await query;

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load teachers');
    }
  };

  // Handle teacher search with debouncing
  useEffect(() => {
    if (!isAdmin) return; // Only for admin users
    
    const timeoutId = setTimeout(() => {
      if (teacherSearch.trim().length >= 2) {
        loadUsers(teacherSearch);
        setShowTeacherResults(true);
      } else if (teacherSearch.trim().length === 0) {
        setShowTeacherResults(false);
        setUsers([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [teacherSearch, isAdmin]);

  const handleTeacherSelect = (teacher: any) => {
    setSelectedTeacher(teacher);
    setSelectedUser(teacher.id);
    setTeacherSearch(`${teacher.first_name} ${teacher.last_name} (${teacher.email})`);
    setShowTeacherResults(false);
  };

  const filteredUsers = users.filter(user => 
    user.first_name.toLowerCase().includes(teacherSearch.toLowerCase()) ||
    user.last_name.toLowerCase().includes(teacherSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(teacherSearch.toLowerCase())
  );

  const loadReportData = async () => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      let startDate, endDate;
      
      switch (selectedPeriod) {
        case 'current':
          startDate = startOfMonth(new Date());
          endDate = endOfMonth(new Date());
          break;
        case 'last':
          startDate = startOfMonth(subMonths(new Date(), 1));
          endDate = endOfMonth(subMonths(new Date(), 1));
          break;
        case 'quarter':
          startDate = startOfMonth(subMonths(new Date(), 3));
          endDate = endOfMonth(new Date());
          break;
        case 'year':
          startDate = startOfMonth(subMonths(new Date(), 12));
          endDate = endOfMonth(new Date());
          break;
        default:
          startDate = startOfMonth(new Date());
          endDate = endOfMonth(new Date());
      }

      // Load meeting statistics
      const { data: meetingData, error: meetingError } = await supabase
        .from('meetings')
        .select('*')
        .or(`observer_id.eq.${selectedUser},observee_id.eq.${selectedUser}`)
        .eq('status', 'completed')
        .gte('scheduled_date', startDate.toISOString())
        .lte('scheduled_date', endDate.toISOString());

      if (meetingError) throw meetingError;

      // Group by meeting type
      const typeStats: { [key: string]: MentoringStats } = {};
      let totalMins = 0;

      meetingData?.forEach(meeting => {
        const type = meeting.meeting_type;
        const minutes = meeting.duration_minutes || 0;
        totalMins += minutes;

        if (!typeStats[type]) {
          typeStats[type] = {
            meeting_type: type,
            total_meetings: 0,
            total_minutes: 0,
            avg_duration: 0
          };
        }

        typeStats[type].total_meetings += 1;
        typeStats[type].total_minutes += minutes;
      });

      // Calculate averages
      Object.values(typeStats).forEach(stat => {
        stat.avg_duration = stat.total_meetings > 0 
          ? Math.round(stat.total_minutes / stat.total_meetings)
          : 0;
      });

      setStats(Object.values(typeStats));
      setTotalMinutes(totalMins);

      // Load monthly trend data for the past 6 months
      const sixMonthsAgo = startOfMonth(subMonths(new Date(), 6));
      const { data: trendData, error: trendError } = await supabase
        .from('meetings')
        .select('*')
        .or(`observer_id.eq.${selectedUser},observee_id.eq.${selectedUser}`)
        .eq('status', 'completed')
        .gte('scheduled_date', sixMonthsAgo.toISOString());

      if (trendError) throw trendError;

      // Group by month
      const monthlyStats: { [key: string]: MonthlyData } = {};
      
      for (let i = 0; i < 6; i++) {
        const month = subMonths(new Date(), i);
        const monthKey = format(month, 'yyyy-MM');
        monthlyStats[monthKey] = {
          month: format(month, 'MMM yyyy'),
          mentoring_minutes: 0,
          coaching_minutes: 0,
          observation_minutes: 0,
          total_meetings: 0
        };
      }

      trendData?.forEach(meeting => {
        const monthKey = format(new Date(meeting.scheduled_date), 'yyyy-MM');
        if (monthlyStats[monthKey]) {
          const minutes = meeting.duration_minutes || 0;
          monthlyStats[monthKey].total_meetings += 1;

          switch (meeting.meeting_type) {
            case 'mentoring':
              monthlyStats[monthKey].mentoring_minutes += minutes;
              break;
            case 'coaching':
              monthlyStats[monthKey].coaching_minutes += minutes;
              break;
            case 'observation':
              monthlyStats[monthKey].observation_minutes += minutes;
              break;
          }
        }
      });

      setMonthlyData(Object.values(monthlyStats).reverse());

    } catch (error) {
      console.error('Error loading report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getMeetingTypeColor = (type: string) => {
    switch (type) {
      case 'mentoring': return 'bg-green-100 text-green-800';
      case 'coaching': return 'bg-blue-100 text-blue-800';
      case 'observation': return 'bg-purple-100 text-purple-800';
      case 'evaluation': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const periods = [
    { value: 'current', label: 'Current Month' },
    { value: 'last', label: 'Last Month' },
    { value: 'quarter', label: 'Last 3 Months' },
    { value: 'year', label: 'Last 12 Months' }
  ];

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Mentoring & Observation Report
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            {isAdmin && (
              <div className="flex-1 relative">
                <label className="text-sm font-medium">Teacher</label>
                <Input
                  value={teacherSearch}
                  onChange={(e) => {
                    setTeacherSearch(e.target.value);
                    if (e.target.value.trim() === '') {
                      setSelectedTeacher(null);
                      setSelectedUser('');
                    }
                  }}
                  onFocus={() => {
                    if (teacherSearch.trim().length >= 2) {
                      setShowTeacherResults(true);
                    }
                  }}
                  placeholder="Type to search teachers..."
                  className="w-full mt-1"
                />
                
                {showTeacherResults && filteredUsers.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredUsers.map((teacher) => (
                      <div
                        key={teacher.id}
                        className="px-4 py-2 hover:bg-accent cursor-pointer text-sm"
                        onClick={() => handleTeacherSelect(teacher)}
                      >
                        <div className="font-medium">
                          {teacher.first_name} {teacher.last_name}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {teacher.email}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {teacherSearch.trim().length >= 2 && filteredUsers.length === 0 && showTeacherResults && (
                  <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg p-4 text-sm text-muted-foreground">
                    No teachers found matching "{teacherSearch}"
                  </div>
                )}
                
                {teacherSearch.trim().length > 0 && teacherSearch.trim().length < 2 && (
                  <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg p-4 text-sm text-muted-foreground">
                    Type at least 2 characters to search
                  </div>
                )}
              </div>
            )}
            
            <div className="flex-1">
              <label className="text-sm font-medium">Period</label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((period) => (
                    <SelectItem key={period.value} value={period.value}>
                      {period.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Minutes</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatMinutes(totalMinutes)}</div>
            <p className="text-xs text-muted-foreground">
              {stats.reduce((sum, s) => sum + s.total_meetings, 0)} meetings
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Mentoring</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMinutes(stats.find(s => s.meeting_type === 'mentoring')?.total_minutes || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.find(s => s.meeting_type === 'mentoring')?.total_meetings || 0} sessions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Coaching</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMinutes(stats.find(s => s.meeting_type === 'coaching')?.total_minutes || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.find(s => s.meeting_type === 'coaching')?.total_meetings || 0} sessions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Observations</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMinutes(stats.find(s => s.meeting_type === 'observation')?.total_minutes || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.find(s => s.meeting_type === 'observation')?.total_meetings || 0} sessions
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Meeting Type Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Meeting Type Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : stats.length > 0 ? (
            <div className="space-y-4">
              {stats.map((stat) => (
                <div key={stat.meeting_type} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge className={getMeetingTypeColor(stat.meeting_type)}>
                      {stat.meeting_type}
                    </Badge>
                    <div>
                      <div className="font-medium">{stat.total_meetings} meetings</div>
                      <div className="text-sm text-muted-foreground">
                        Avg {stat.avg_duration}min per session
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">{formatMinutes(stat.total_minutes)}</div>
                    <div className="text-sm text-muted-foreground">total time</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No completed meetings found for the selected period.
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI Tracking */}
      <MeetingKPITracker 
        selectedPeriod={selectedPeriod}
        selectedUser={selectedUser}
      />

      {/* Monthly Trend */}
      <Card>
        <CardHeader>
          <CardTitle>6-Month Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {monthlyData.map((month) => (
              <div key={month.month} className="flex items-center justify-between p-3 border rounded">
                <div className="font-medium">{month.month}</div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span>Mentoring: {formatMinutes(month.mentoring_minutes)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span>Coaching: {formatMinutes(month.coaching_minutes)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <span>Observation: {formatMinutes(month.observation_minutes)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}